'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { Button, HStack, Text } from '@chakra-ui/react';
import { useAuth } from '@/app/auth-provider';
import { AppLayout } from '@/components/core/app-layout/AppLayout';
import { AppContentLoading, AppRouteLoading } from '@/components/shared/AppRouteLoading';
import { useStripeRole } from '@/hooks/useStripeRole';
import { useProject, useWorkspaceManagementStatus } from '@/contexts/ProjectContext';

interface AppSectionGateProps {
  children: React.ReactNode;
  initialAccess?: {
    uid: string;
    isAuthenticated: boolean;
    hasBasicAccess: boolean;
  } | null;
  initialIsAdmin?: boolean;
}

const PAYWALL_PATH = '/app/paywall';

type AppGatePhase =
  | 'resolving_access'
  | 'redirecting_to_get_started'
  | 'redirecting_to_paywall'
  | 'redirecting_to_app'
  | 'render_paywall'
  | 'render_app';

export function AppSectionGate({
  children,
  initialAccess = null,
  initialIsAdmin = false,
}: AppSectionGateProps) {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const checkoutParam = searchParams.get('checkout');
  const { user, loading: authLoading } = useAuth();
  const { hasBasicAccess, loading: roleLoading } = useStripeRole();

  const isPaywallRoute = pathname === PAYWALL_PATH;
  const isAuthenticated = !!user || (authLoading && !!initialAccess?.isAuthenticated);
  const isCheckoutSuccess = checkoutParam === 'success';
  const isAccessStillResolving = authLoading || (!!user && roleLoading);
  const initialIdentityMatches =
    initialAccess !== null && ((!user && authLoading) || user?.uid === initialAccess.uid);
  const canUseInitialAccess =
    isAccessStillResolving &&
    initialIdentityMatches &&
    initialAccess.isAuthenticated &&
    !isCheckoutSuccess;
  const hasBasicAccessResolved = canUseInitialAccess
    ? initialAccess.hasBasicAccess
    : hasBasicAccess;
  const shouldShowSpinner = isAccessStillResolving && !canUseInitialAccess;
  const activeInitialIsAdmin = initialIdentityMatches ? initialIsAdmin : false;
  const gatePhase: AppGatePhase = (() => {
    if (shouldShowSpinner) {
      return 'resolving_access';
    }
    if (!isAuthenticated) {
      return 'redirecting_to_get_started';
    }
    if (!hasBasicAccessResolved) {
      return isPaywallRoute ? 'render_paywall' : 'redirecting_to_paywall';
    }
    if (isPaywallRoute) {
      return 'redirecting_to_app';
    }
    return 'render_app';
  })();

  useEffect(() => {
    if (gatePhase === 'redirecting_to_get_started') {
      const query = searchParams.toString();
      router.replace(`/get-started?url=${encodeURIComponent(`${pathname}${query ? `?${query}` : ''}`)}`);
      return;
    }
    if (gatePhase === 'redirecting_to_paywall') {
      router.replace(PAYWALL_PATH);
      return;
    }
    if (gatePhase === 'redirecting_to_app') {
      router.replace('/app');
    }
  }, [gatePhase, router, pathname, searchParams]);

  if (gatePhase === 'resolving_access' && !isAuthenticated) {
    return <AppRouteLoading />;
  }

  if (gatePhase === 'render_paywall') {
    return <>{children}</>;
  }

  if (gatePhase === 'render_app') {
    return (
      <AppLayout initialIsAdmin={activeInitialIsAdmin}>
        <WorkspaceManagementNotice />
        {children}
      </AppLayout>
    );
  }

  return (
    <AppLayout initialIsAdmin={activeInitialIsAdmin} authPreview={!isAuthenticated}>
      <AppContentLoading dataSection="appGateLoadingContent" />
    </AppLayout>
  );
}

function WorkspaceManagementNotice() {
  const { manageableWorkspacesError } = useWorkspaceManagementStatus();
  const { fetchProjects } = useProject();
  if (!manageableWorkspacesError) return null;
  return (
    <HStack role="alert" px="4" py="2" gap="3">
      <Text fontSize="sm">Workspace options couldn’t load.</Text>
      <Button size="xs" variant="outline" onClick={() => void fetchProjects(true)}>
        Retry
      </Button>
    </HStack>
  );
}
